import * as React from 'react';
import { useState } from 'react';
import {
    MapMarkerIcon
} from '@patternfly/react-icons';

export interface IOrganizationCardProps {
    organization: {
        id: string;
        organizationName: string;
        category?: string;
        researchStatus?: 'NONE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
        address?: {
            city?: string;
            state?: string;
            country?: string;
        };
    };
    onClick?: (organization: any) => void;
}

const OrganizationCard: React.FunctionComponent<IOrganizationCardProps> = ({
    organization,
    onClick
}) => {
    const [isHovered, setIsHovered] = useState(false);

    // Generate gradient based on organization name
    const getGradientFromName = (name: string) => {
        const gradients = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
    };

    const gradient = getGradientFromName(organization.organizationName || 'Org');
    const initial = (organization.organizationName || 'O').charAt(0).toUpperCase();

    const getLocationText = () => {
        if (!organization.address) return null;
        const parts = [organization.address.city, organization.address.state].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : null;
    };

    const locationText = getLocationText();

    const getResearchBadge = () => {
        const status = organization.researchStatus;
        if (status === 'COMPLETED') {
            return { label: 'Researched', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
        }
        if (status === 'IN_PROGRESS') {
            return { label: 'Researching...', bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe', spinning: true };
        }
        return { label: 'Not researched', bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
    };

    const badge = getResearchBadge();

    const styles = {
        card: {
            background: '#ffffff',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: isHovered
                ? '0 12px 24px rgba(0, 0, 0, 0.15)'
                : '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
            cursor: 'pointer'
        },
        header: {
            background: gradient,
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            position: 'relative' as const
        },
        initialCircle: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 700,
            color: '#ffffff',
            flexShrink: 0
        },
        headerContent: {
            flex: 1,
            minWidth: 0
        },
        orgName: {
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 600,
            margin: 0,
            whiteSpace: 'nowrap' as const,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        category: {
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '12px',
            margin: 0,
            textTransform: 'capitalize' as const
        },
        badgeContainer: {
            position: 'absolute' as const,
            top: '10px',
            right: '10px'
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '3px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            background: badge.bg,
            color: badge.color,
            border: `1px solid ${badge.border}`
        },
        body: {
            padding: '16px 24px'
        },
        locationRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#6b7280',
            fontSize: '13px'
        },
        locationIcon: {
            color: '#667eea'
        },
        expandHint: {
            padding: '0 24px 14px',
            fontSize: '12px',
            color: '#9ca3af',
            textAlign: 'center' as const,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s'
        },
        spinner: {
            display: 'inline-block',
            width: '10px',
            height: '10px',
            border: '2px solid #bfdbfe',
            borderTopColor: '#1e40af',
            borderRadius: '50%',
            animation: 'orgCardSpin 0.8s linear infinite'
        }
    };

    return (
        <>
            <div
                style={styles.card}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={() => onClick && onClick(organization)}
            >
                <div style={styles.header}>
                    <div style={styles.initialCircle}>
                        {initial}
                    </div>
                    <div style={styles.headerContent}>
                        <h3 style={styles.orgName}>{organization.organizationName}</h3>
                        <p style={styles.category}>{organization.category || 'Business'}</p>
                    </div>
                    <div style={styles.badgeContainer}>
                        <span style={styles.badge}>
                            {'spinning' in badge && badge.spinning && <span style={styles.spinner} />}
                            {badge.label}
                        </span>
                    </div>
                </div>
                {locationText && (
                    <div style={styles.body}>
                        <div style={styles.locationRow}>
                            <MapMarkerIcon style={styles.locationIcon} />
                            <span>{locationText}</span>
                        </div>
                    </div>
                )}
                <div style={styles.expandHint}>Click to expand</div>
            </div>
            <style>{`
                @keyframes orgCardSpin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export { OrganizationCard };
